import React from 'react';
import { getOperarioNombre, formatDateTime, calculateElapsedTime, formatHoursAndMinutes, formatDuration, checkStockAvailability } from '../utils/helpers';
import OperationProgressBar from './OperationProgressBar';
import { defaultMateriales } from '../mock/materiales'; // Importar materiales

const DashboardOperationList = ({ 
  operaciones, operarios, title, filterStatus, currentTime, 
  onPauseOperationRequest, onFinishOperationRequest, onStartPendingOperation, 
  filterCentroTrabajoId, // Usado para filtrar, pero la UI está en DashboardPlantWorkCenterFilters
  plantas, selectedPlantFilterId, // Usado para filtrar, pero la UI está en DashboardPlantWorkCenterFilters
  onResumeOperation,
  // Props para los filtros internos de esta lista (solo si es una lista del dashboard principal)
  selectedProductFilter, onProductFilterChange,
  selectedOperarioFilterId, onOperarioFilterChange,
  onConsumptionClick // Nueva prop para abrir el modal de consumos
}) => {
  // Calcular productos disponibles para el filtro de botones
  const productsForFilter = [...new Set(
    operaciones
      .filter(op => {
        const statusMatch = op.estado === filterStatus;
        const centroTrabajoMatch = filterCentroTrabajoId ? op.centroTrabajoId === filterCentroTrabajoId : true;
        const plantFilterMatch = selectedPlantFilterId 
          ? (plantas.find(p => p.id === selectedPlantFilterId)?.centrosTrabajo.map(ct => ct.id) || []).includes(op.centroTrabajoId)
          : true;
        return statusMatch && centroTrabajoMatch && plantFilterMatch;
      })
      .map(op => op.producto)
  )].sort(); // Ordenar alfabéticamente

  // Calcular operarios relevantes para el filtro de botones
  const relevantOperarios = [...new Set(
    operaciones
      .filter(op => {
        const statusMatch = op.estado === filterStatus;
        const centroTrabajoMatch = filterCentroTrabajoId ? op.centroTrabajoId === filterCentroTrabajoId : true;
        const plantFilterMatch = selectedPlantFilterId 
          ? (plantas.find(p => p.id === selectedPlantFilterId)?.centrosTrabajo.map(ct => ct.id) || []).includes(op.centroTrabajoId)
          : true;
        return statusMatch && centroTrabajoMatch && plantFilterMatch;
      })
      .map(op => op.operarioId)
  )].map(id => operarios.find(op => op.id === id)).filter(Boolean); // Filtrar operarios válidos

  const filteredOperations = operaciones.filter(op => {
    const statusMatch = op.estado === filterStatus;
    const centroTrabajoMatch = filterCentroTrabajoId ? op.centroTrabajoId === filterCentroTrabajoId : true;
    const plantFilterMatch = selectedPlantFilterId 
      ? (plantas.find(p => p.id === selectedPlantFilterId)?.centrosTrabajo.map(ct => ct.id) || []).includes(op.centroTrabajoId)
      : true;
    
    // Aplicar filtros internos solo si no estamos en la vista de plantas (filterCentroTrabajoId es null)
    const operarioFilterMatch = (!filterCentroTrabajoId && selectedOperarioFilterId) ? op.operarioId === selectedOperarioFilterId : true;
    const productFilterMatch = (!filterCentroTrabajoId && selectedProductFilter) ? op.producto === selectedProductFilter : true;

    return statusMatch && centroTrabajoMatch && plantFilterMatch && operarioFilterMatch && productFilterMatch;
  });

  // Condición para mostrar los filtros internos (producto y operario)
  const showInternalFilters = !filterCentroTrabajoId; // Solo mostrar si es una lista del dashboard principal

  const operationsLayoutClass = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4';

  return (
    <div className="bg-white p-4 rounded-2xl shadow-lg transition-colors duration-300">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>
      
      {showInternalFilters && (
        <div className="flex flex-col sm:flex-row gap-3 mb-3">
          {/* Filtro por Producto con botones */}
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-medium mb-2">Por Producto:</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onProductFilterChange('')}
                className={`py-1.5 px-3 rounded-lg font-semibold transition-colors text-sm ${
                  selectedProductFilter === ''
                    ? 'bg-black text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Todos
              </button>
              {productsForFilter.map(product => (
                <button
                  key={product}
                  onClick={() => onProductFilterChange(product)}
                  className={`py-1.5 px-3 rounded-lg font-semibold transition-colors text-sm ${
                    selectedProductFilter === product
                      ? 'bg-black text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {product}
                </button>
              ))}
            </div>
          </div>

          {/* Filtro por Operario con botones */}
          <div className="flex-1">
            <label className="block text-gray-700 text-sm font-medium mb-2">Por Operario:</label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onOperarioFilterChange('')}
                className={`py-1.5 px-3 rounded-lg font-semibold transition-colors text-sm ${
                  selectedOperarioFilterId === ''
                    ? 'bg-black text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Todos
              </button>
              {relevantOperarios.map(op => (
                <button
                  key={op.id}
                  onClick={() => onOperarioFilterChange(op.id)}
                  className={`py-1.5 px-3 rounded-lg font-semibold transition-colors text-sm ${
                    selectedOperarioFilterId === op.id
                      ? 'bg-black text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {op.nombre}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {filteredOperations.length === 0 ? (
        <p className="text-gray-500 text-center py-4 text-sm">No hay operaciones {filterStatus} en este filtro.</p>
      ) : (
        <div className={operationsLayoutClass}>
          {filteredOperations.map(op => {
            const elapsedTime = op.estado === 'iniciada'
              ? calculateElapsedTime(op.inicio, currentTime, op.totalTiempoPausadoMs)
              : calculateElapsedTime(op.inicio, new Date(op.ultimaPausaInicio || op.inicio), op.totalTiempoPausadoMs);

            const estimatedTime = op.tiempoEstimadoHoras;
            const { isStockOk } = checkStockAvailability(op.consumos, defaultMateriales); // Verificar stock

            return (
              <div key={op.id} className={`p-4 rounded-xl border border-gray-200 flex flex-col transition-colors duration-300 ${isStockOk ? 'bg-gray-50' : 'bg-red-50'}`}>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <p className="text-lg font-bold text-gray-800">{op.producto} - <span className="text-base text-indigo-600">{getOperarioNombre(op.operarioId, operarios)}</span></p>
                    <p className="text-sm text-gray-500 mt-1">ID: {op.id}</p>
                    {op.inicio && <p className="text-sm text-gray-500">Inicio: {formatDateTime(op.inicio)}</p>}
                    {op.fin && <p className="text-sm text-gray-500">Fin: {formatDateTime(op.fin)}</p>}
                    {op.unidades && <p className="text-sm text-gray-500">Unidades Fabricadas: {op.unidades}</p>}
                    {op.targetUnits && <p className="text-sm text-gray-500">Unidades a Fabricar: {op.targetUnits}</p>}
                    
                    {/* Indicador de Stock */}
                    {(op.estado === 'pendiente' || op.estado === 'iniciada' || op.estado === 'pausada') && (
                      <div className={`flex items-center text-sm mt-1 px-2 py-1 rounded-md ${isStockOk ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {isStockOk ? (
                          <span className="text-emerald-500 mr-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </span>
                        ) : (
                          <span className="text-red-500 mr-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                        <span className={`${isStockOk ? 'text-emerald-800' : 'text-red-800'}`}>{isStockOk ? 'Stock OK' : 'Falta Stock'}</span>
                      </div>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    op.estado === 'iniciada' ? 'bg-indigo-100 text-indigo-800' :
                    op.estado === 'pendiente' ? 'bg-amber-100 text-amber-800' :
                    op.estado === 'pausada' ? 'bg-red-100 text-red-800' :
                    'bg-emerald-100 text-emerald-800'
                  }`}>
                    {op.estado.charAt(0).toUpperCase() + op.estado.slice(1)}
                  </span>
                </div>
                {(op.estado === 'iniciada' || op.estado === 'pausada' || op.estado === 'pendiente') && (
                  <div className="mt-2">
                    {op.estado !== 'pendiente' && estimatedTime > 0 ? (
                      <p className="text-sm text-gray-600">
                        Tiempo total: <span className="font-bold text-gray-800">{estimatedTime}h</span> Tiempo actual: <span className="font-bold text-gray-800">{formatDuration(elapsedTime)}</span>
                      </p>
                    ) : (
                      op.estado !== 'pendiente' && <p className="text-sm text-gray-600">Tiempo de trabajo: <span className="font-bold text-gray-800">{formatDuration(elapsedTime)}</span></p>
                    )}
                    {op.estado !== 'pendiente' && estimatedTime > 0 && (
                      <OperationProgressBar
                        elapsedTimeSeconds={elapsedTime}
                        estimatedTotalHours={estimatedTime}
                      />
                    )}
                    <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0 mt-3">
                      {/* Botón de Consumos a la izquierda */}
                      <button
                        onClick={() => onConsumptionClick(op.id, op.consumos)}
                        className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm"
                      >
                        Consumos
                      </button>
                      {op.estado === 'iniciada' ? (
                        <button
                          onClick={() => onPauseOperationRequest(op.id, op.unidades || 0)}
                          className="flex-1 py-2 rounded-lg font-semibold transition-colors bg-yellow-500 hover:bg-yellow-600 text-white text-sm"
                        >
                          Pausar
                        </button>
                      ) : op.estado === 'pausada' ? (
                        <button
                          onClick={() => onResumeOperation(op.id)}
                          className="flex-1 py-2 rounded-lg font-semibold transition-colors bg-green-500 hover:bg-green-600 text-white text-sm"
                        >
                          Reanudar
                        </button>
                      ) : ( // Estado pendiente
                        <button
                          onClick={() => onStartPendingOperation(op.id)}
                          className="flex-1 py-2 rounded-lg font-semibold transition-colors bg-indigo-600 text-white hover:bg-indigo-700 text-sm"
                        >
                          Iniciar Operación
                        </button>
                      )}
                      {(op.estado === 'iniciada' || op.estado === 'pausada') && (
                        <button
                          onClick={() => onFinishOperationRequest(op.id, op.unidades || 0)}
                          className="flex-1 bg-black text-white py-2 rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm"
                        >
                          Finalizar
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardOperationList;